import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Lock, Play, Image as ImageIcon, Coins, ChatCircle, User, Flag } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ReportModal from '../components/ReportModal';

const API = process.env.REACT_APP_BACKEND_URL;

const FeedPage = () => {
  const { user, refreshUser } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);

  const fetchFeed = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/content/feed`, { withCredentials: true });
      setContent(response.data);
    } catch (err) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handleUnlock = async (contentId, tokens) => {
    if ((user?.tokens || 0) < tokens) {
      toast.error('Insufficient tokens');
      return;
    }
    
    setUnlocking(contentId);
    try {
      const response = await axios.post(
        `${API}/api/content/unlock`,
        { content_id: contentId },
        { withCredentials: true }
      );
      
      // Update content with unlocked media
      setContent(prev => prev.map(c => 
        c.id === contentId 
          ? { ...c, is_locked: false, media_url: response.data.media_url }
          : c
      ));
      
      await refreshUser();
      toast.success('Content unlocked!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setUnlocking(null);
    }
  };

  const handleLike = async (contentId) => {
    try {
      const response = await axios.post(
        `${API}/api/content/like/${contentId}`,
        {},
        { withCredentials: true }
      );
      
      setContent(prev => prev.map(c => 
        c.id === contentId 
          ? { ...c, likes_count: c.likes_count + (response.data.liked ? 1 : -1) }
          : c
      ));
    } catch (err) {
      toast.error('Failed to like');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Sexy Hero Banner */}
      {content.length === 0 && (
        <div className="relative rounded-3xl overflow-hidden mb-8" data-testid="feed-hero-banner">
          <img
            src="https://images.unsplash.com/photo-1565090474977-b4eefefd0c90?w=1200&q=80&auto=format"
            alt=""
            className="w-full h-48 sm:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b]/90 via-[#09090b]/60 to-transparent" />
          <div className="absolute inset-0 flex items-center p-6 sm:p-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
                Your feed awaits
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mb-4 max-w-xs">
                Follow creators to fill your feed with exclusive content
              </p>
              <Link
                to="/explore"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-full font-semibold text-sm hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/25"
              >
                Explore Creators
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-white">Feed</h1>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 rounded-full">
          <Coins weight="duotone" className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-amber-400">{user?.tokens || 0}</span>
        </div>
      </div>

      {/* Empty State */}
      {content.length === 0 && (
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-full bg-slate-900 border border-white/5 flex items-center justify-center mx-auto mb-4">
            <Heart weight="duotone" className="w-10 h-10 text-blue-500/40" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Nothing here yet</h2>
          <p className="text-slate-500 mb-6 max-w-sm mx-auto">Discover and follow creators to start seeing their exclusive content in your feed</p>
          <Link
            to="/explore"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all"
            data-testid="feed-explore-cta"
          >
            Explore Creators
          </Link>
        </div>
      )}

      {/* Content Grid */}
      <div className="space-y-6">
        <AnimatePresence>
          {content.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative overflow-hidden rounded-3xl bg-slate-900 border border-white/5"
              data-testid={`content-card-${item.id}`}
            >
              {/* Creator Info */}
              <Link 
                to={`/profile/${item.creator_id}`}
                className="flex items-center gap-3 p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-slate-800 overflow-hidden">
                  {item.creator_avatar ? (
                    <img src={item.creator_avatar} alt={item.creator_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{item.creator_name}</p>
                  <p className="text-xs text-slate-500">{new Date(item.created_at).toLocaleDateString()}</p>
                </div>
              </Link>

              {/* Media */}
              <div className="relative aspect-[4/5] bg-slate-950">
                {item.is_locked ? (
                  <>
                    {/* Blurred Preview */}
                    <img 
                      src={item.thumbnail_url}
                      alt={item.title}
                      className="w-full h-full object-cover content-locked"
                    />
                    {/* Lock Overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 backdrop-blur-sm">
                      <div className="glass rounded-2xl p-6 text-center">
                        <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                          <Lock weight="duotone" className="w-7 h-7 text-blue-500" />
                        </div>
                        <p className="text-white font-semibold mb-2">Premium Content</p>
                        <p className="text-sm text-slate-400 mb-4">Unlock for {item.price_tokens} tokens</p>
                        <button
                          onClick={() => handleUnlock(item.id, item.price_tokens)}
                          disabled={unlocking === item.id}
                          className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-all btn-primary-glow disabled:opacity-50"
                          data-testid={`unlock-btn-${item.id}`}
                        >
                          {unlocking === item.id ? (
                            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Coins weight="duotone" className="w-5 h-5" />
                              <span>Unlock</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {item.content_type === 'video' ? (
                      <div className="relative w-full h-full">
                        <img 
                          src={item.thumbnail_url || item.media_url}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Play weight="fill" className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={item.media_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="p-4">
                <div className="flex items-center gap-4 mb-3">
                  <button
                    onClick={() => handleLike(item.id)}
                    className="flex items-center gap-1.5 text-slate-400 hover:text-blue-500 transition-colors"
                    data-testid={`like-btn-${item.id}`}
                  >
                    <Heart weight="duotone" className="w-6 h-6" />
                    <span className="text-sm font-medium">{item.likes_count}</span>
                  </button>
                  <button className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors">
                    <ChatCircle weight="duotone" className="w-6 h-6" />
                    <span className="text-sm font-medium">{item.comments_count}</span>
                  </button>
                  <button
                    onClick={() => setReportTarget({ type: 'content', id: item.id, name: item.title })}
                    className="ml-auto flex items-center gap-1.5 text-slate-600 hover:text-amber-400 transition-colors"
                    data-testid={`report-btn-${item.id}`}
                    title="Report content"
                  >
                    <Flag className="w-5 h-5" />
                  </button>
                </div>
                
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-slate-400 line-clamp-2">{item.description}</p>
                )}
                
                {item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {item.tags.map(tag => (
                      <span 
                        key={tag}
                        className="px-2 py-1 text-xs bg-slate-800 text-slate-400 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Report Modal */}
      <ReportModal
        open={!!reportTarget}
        onOpenChange={(open) => { if (!open) setReportTarget(null); }}
        targetType={reportTarget?.type || 'content'}
        targetId={reportTarget?.id || ''}
        targetName={reportTarget?.name || ''}
      />
    </div>
  );
};

export default FeedPage;
